import Vue from "vue";
import App from "./App.vue";
import "./registerServiceWorker";
import "./directives/format-amount";
import router from "./router";
import store from "./store";
import vuetify from "./plugins/vuetify";
import Cookies from "js-cookie";
import VueApexCharts from "vue-apexcharts";
import VueClipboard from "vue-clipboard2";
import { VueReCaptcha } from "vue-recaptcha-v3";
import Toast from "vue-toastification";
import VueFileAgent from "vue-file-agent";
import "./firebase-fcm";
// eslint-disable-next-line no-unused-vars
import VueFileAgentStyles from "vue-file-agent/dist/vue-file-agent.css";

// Import the CSS or use your own
import "vue-toastification/dist/index.css";

const SENSITIVE_LOG_KEYS = new Set([
  "accountid",
  "account_id",
  "accountnumber",
  "account_number",
  "acctno",
  "authcode",
  "authid",
  "authtoken",
  "bizid",
  "businessid",
  "confirmpass",
  "ctoken",
  "email",
  "memberid",
  "newpass",
  "otp",
  "otpcode",
  "pass",
  "password",
  "phone",
  "phoneno",
  "pword",
  "resetcode",
  "secret",
  "secretcode",
  "sesscode",
  "sessionid",
  "token",
  "transpin",
  "userid",
  "user_id",
  "useremail",
]);

function installConsoleRedaction() {
  const nativeConsole = {};
  ["log", "info", "warn", "error", "debug", "trace"].forEach((method) => {
    if (typeof console[method] === "function") {
      nativeConsole[method] = console[method].bind(console);
    }
  });

  const maskEmail = (value) => {
    const [name, domain] = String(value).split("@");
    if (!domain) return value;
    return `${name.slice(0, 2)}***@${domain}`;
  };

  const maskPhone = (value) => {
    const digits = String(value).replace(/\D+/g, "");
    if (digits.length <= 4) return "***";
    return `${digits.slice(0, 3)}***${digits.slice(-3)}`;
  };

  const maskString = (value) => {
    const input = String(value);
    let masked = input
      .replace(/([?&](?:account(?:_?id)?|user(?:_?id)?|member(?:_?id)?|business(?:_?id)?|bizid|token|sesscode|sessionid|otp(?:code)?|pass(?:word)?|confirmpass|newpass|pin|transpin|secret(?:code)?|auth(?:id|code|token)?|email|phone(?:no)?|acct(?:no|number)?)=)([^&\s]+)/gi, "$1[redacted]")
      .replace(/\b[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}\b/g, (match) => maskEmail(match))
      .replace(/\b(?:\+?\d[\d\s().-]{7,}\d)\b/g, (match) => maskPhone(match));

    if (/^\d{6,}$/.test(masked.trim())) {
      const trimmed = masked.trim();
      masked = `${trimmed.slice(0, 2)}***${trimmed.slice(-2)}`;
    }

    return masked;
  };

  const maskValue = (value, depth = 0) => {
    if (value == null) return value;
    if (typeof value === "string") return maskString(value);
    if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
      return value;
    }
    if (typeof value === "function") return "[function]";
    if (value instanceof Error) {
      const copy = new Error(maskString(value.message));
      copy.name = value.name;
      if (typeof value.stack === "string") {
        copy.stack = maskString(value.stack);
      }
      return copy;
    }
    if (typeof Blob !== "undefined" && value instanceof Blob) return value;
    if (typeof File !== "undefined" && value instanceof File) return value;
    if (Array.isArray(value)) {
      if (depth > 3) return "[array]";
      return value.map((item) => maskValue(item, depth + 1));
    }
    if (typeof value === "object") {
      if (depth > 3) return "[object]";
      const safe = {};
      for (const [key, item] of Object.entries(value)) {
        const normalizedKey = String(key).replace(/[^a-z0-9]/gi, "").toLowerCase();
        safe[key] = SENSITIVE_LOG_KEYS.has(normalizedKey) ? "[redacted]" : maskValue(item, depth + 1);
      }
      return safe;
    }
    return value;
  };

  Object.keys(nativeConsole).forEach((method) => {
    console[method] = (...args) => nativeConsole[method](...args.map((arg) => maskValue(arg)));
  });
}

installConsoleRedaction();

const options = {
  timeout: 4500,
  closeOnClick: true,
  pauseOnHover: false,
  pauseOnFocusLoss: false,
  draggable: false,
  showCloseButtonOnHover: false,
};
Vue.use(Toast, options);

Vue.use(VueFileAgent);

// import firebaseMessaging from "./firebase";

// Vue.prototype.$messaging = firebaseMessaging;

Vue.use(VueApexCharts);
Vue.component("apexchart", VueApexCharts);
VueClipboard.config.autoSetContainer = true;
Vue.use(VueClipboard);
Vue.use(VueReCaptcha, {
  siteKey: "6LeK0oklAAAAAA8oljzNi1Aef2H4pIIRDrCrTjM9",
  loaderOptions: { autoHideBadge: true },
});

Vue.config.productionTip = false;

export const bus = new Vue();

export const availableRoles = [
  "admin",
  "operations",
  "manager",
  "cashier",
  "developer",
  "customer support",
  "human resources",
  "accountant",
];

//verify access on routing to each page
router.beforeEach(async (to, from, next) => {
  try {
    //redirect to login from 2fa page if user not logged in
    if (
      to.meta.auth === "login" &&
      (!store.getters.getUserId || !store.getters.getUser)
    ) {
      next({ name: "home" });
      return;
    }

    //redirect to login page for unauthorized access
    if (
      to.meta.auth === "user" &&
      (store.getters.getUser === "" || !Cookies.get("user-logged"))
    ) {
      next({ name: "home" });
      return;
    }

    if (
      to.meta.auth === "user" &&
      Cookies.get("user-logged") &&
      to.name !== "account-verification"
    ) {
      const verstatus = store.getters.getCache?.verstatus || {};
      if (!verstatus?.compliance_required && !verstatus?.compliance_pending) {
        await store.dispatch("fetchVerificationStatus").catch(() => null);
      }
      const latestVerstatus = store.getters.getCache?.verstatus || {};
      if (latestVerstatus?.compliance_required || latestVerstatus?.compliance_pending) {
        next({ name: "account-verification" });
        return;
      }
    }

    //role verification before going to a route
    if (to.meta.auth === "user" && Cookies.get("user-logged")) {
      //if page does not have a role
      if (to.meta.roles === undefined) {
        return next();
      }

      //fetch user details
      if (store.getters.getUserDetails === "") {
        await store.dispatch("fetchUserCredentials");
      }

      //redirect users to their rightful dashboard
      if (to.name === "dashboard") {
        const activeRole = `${store.getters.getUser?.role || store.getters.getUserDetails?.myrole || ""}`.trim().toLowerCase();
        if (activeRole === "cashier")
          return next({ name: "cashierTransactions" });
        if (activeRole === "manager")
          return next({ name: "businessDashboard" });
        if (activeRole === "human resources" || activeRole === "human_resources")
          return next({ name: "payroll" });
        if (activeRole === "cashiermfi")
          return next({ name: "cashier-mfi" });
        if (activeRole === "operations_manager" || activeRole === "operations manager")
          return next({ name: "manager-mfi" });
        if (activeRole === "customer")
          return next({ name: "subaccount-dashboard" });
      }

      let userRole = `${store.getters.getUser?.role || store.getters.getUserDetails?.myrole || ""}`.trim().toLowerCase();

      let canAccess = to.meta.roles.indexOf(userRole) !== -1;
      if (canAccess) return next();
      return next({ name: "unauthorized" });
    } else next();
  } catch (e) {
    // return false
    console.log(e.message);
  }
});

//set the title of each webpage on routing and redirect to the top
router.afterEach((toRoute) => {
  window.document.title =
    toRoute.meta && toRoute.meta.title
      ? `${toRoute.meta.title} | Boldd`
      : "Boldd";
  window.scroll(0, 0);
});

router.beforeEach((to, from, next) => {
  // prevent further redirection if already on target page
  if (to.name === from.name) {
    next();
  } else if (from.query.business && !to.query.business) {
    next({ path: to.path, query: { business: from.query.business } });
    return;
  } else {
    next();
  }
});

router.beforeEach((to, from, next) => {
  if (to.name) {
    // eslint-disable-next-line no-undef
    NProgress.start("1.0");
  }
  next();
});

// ?? For splash screen
router.afterEach(() => {
  // Remove initial loading
  // eslint-disable-next-line no-undef
  NProgress.done();
  const appLoading = document.getElementById("loading-bg");
  if (appLoading) {
    appLoading.style.display = "none";
  }
});

// Prevent the contextmenu event(right-click)
Vue.directive("disable-right-click", {
  bind: function (el) {
    el.addEventListener("contextmenu", function (e) {
      e.preventDefault();
    });
  },
});

new Vue({
  router,
  store,
  vuetify,
  render: (h) => h(App),
}).$mount("#app");
